import React, { useEffect, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AnmerkungTextEditor } from '@/pages/Konfiguration/DokumentTypen/components/AnmerkungTextEditor';
import { DokumentTypFormInput } from '@/pages/Konfiguration/DokumentTypen/schemas/dokumenttypenerstellen';

export const AnmerkungenBox: React.FC<{
  form: UseFormReturn<DokumentTypFormInput>;
}> = ({ form }) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = form;
  const [confirmDeaktivieren, setConfirmDeaktivieren] = useState(false);
  const anmerkungenFreigeschaltet = useFeatureFlag()('CORE_7497_ANMERKUNGEN');

  const anmerkungAktiv = watch('anmerkungAktiv');
  const eventTypOrder = watch('eventTyp') === 'order';

  const setAktiv = (aktiv: boolean) => {
    const options = { shouldDirty: true, shouldValidate: true };
    setValue('anmerkungAktiv', aktiv, options);
    if (!aktiv) {
      setValue('anmerkungBetreff', '', options);
      setValue('anmerkungText', '', options);
    }
  };

  useEffect(() => {
    if (eventTypOrder) {
      setValue('anmerkungAktiv', false);
      setValue('anmerkungBetreff', '');
      setValue('anmerkungText', '');
    }
  }, [eventTypOrder, setValue]);

  const onToggle = (checked: boolean) => {
    if (
      !checked &&
      (getValues('anmerkungBetreff') || getValues('anmerkungText'))
    ) {
      setConfirmDeaktivieren(true);
      return;
    }
    setAktiv(checked);
  };

  if (!anmerkungenFreigeschaltet) return null;

  return (
    <div className="bg-white border border-table-border rounded-md p-4">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            Anmerkungen
          </h2>
          <p className="text-sm text-text-secondary">
            {eventTypOrder
              ? 'Für den Eventtyp order sind keine Anmerkungen möglich.'
              : 'Steuerung, ob zu Vorgängen dieses Dokumenttyps eine Anmerkung am Vertrag in powercloud erstellt werden soll.'}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
          Anmerkungen aktivieren
          <Switch
            checked={!!anmerkungAktiv}
            disabled={eventTypOrder}
            onCheckedChange={onToggle}
          />
        </label>
      </div>

      <fieldset
        disabled={!anmerkungAktiv}
        className="disabled:opacity-50 grid grid-cols-1 md:grid-cols-3 gap-3"
      >
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Betreff <span className="text-red-700">*</span>
          </span>
          <Controller
            control={control}
            name="anmerkungBetreff"
            render={({ field }) => (
              <AnmerkungTextEditor
                singleLine
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                readOnly={!anmerkungAktiv}
                ariaLabel="Betreff"
              />
            )}
          />
          <span className="text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
            {errors.anmerkungBetreff?.message}
          </span>
        </div>

        <div className="md:col-span-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Anmerkungstext <span className="text-red-700">*</span>
          </span>
          <Controller
            control={control}
            name="anmerkungText"
            render={({ field }) => (
              <AnmerkungTextEditor
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                readOnly={!anmerkungAktiv}
                ariaLabel="Anmerkungstext"
              />
            )}
          />
          <span className="text-xs text-text-secondary">
            Variablen aus den Dokumentquelldaten als {'${pfad.zum.feld}'}{' '}
            einfügen – nach {'${'} werden passende Felder vorgeschlagen.
          </span>
          <span className="block text-xs font-semibold uppercase tracking-wide text-red-700 mt-1 min-h-4">
            {errors.anmerkungText?.message}
          </span>
        </div>
      </fieldset>

      <p className="text-sm text-text-secondary mt-2">
        Anmerkungstyp an powercloud:{' '}
        <strong className="text-text-primary">Notiz (Standard)</strong>
      </p>

      <AlertDialog
        open={confirmDeaktivieren}
        onOpenChange={setConfirmDeaktivieren}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anmerkungen deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Beim Deaktivieren werden der gepflegte Betreff und der
              Anmerkungstext gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => setAktiv(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
